import { getUser } from "@/lib/utils/user"
import * as Amplitude from "@amplitude/analytics-browser"

/**
 * Track when an error occurs in the SubscribeModal.
 *
 * @param blogId - The ID of the blog that the error occurred on.
 * @param error - The error that occurred.
 */
export function subscribeError(blogId: string, error: string) {
  return Amplitude.track("subscribe_modal_error", {
    blogId,
    error,
  })
}

/**
 * Track when the user is already logged in with an email.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param email - The email that the user is already logged in with.
 */
export function alreadyLoggedInWithEmail(blogId: string, email: string) {
  return Amplitude.track("subscribe_modal_already_logged_in_with_email", {
    blogId,
    email,
  })
}

/**
 * Track when the user clicks the "Next" button in the EnterEmail step.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param email - The email that the user is entering.
 */
export function emailSubmitted(blogId: string, email: string) {
  return Amplitude.track("subscribe_modal_email_submitted", {
    blogId,
    email,
  })
}

/**
 * Track when the user clicks the "Create account" button in the CreateAccount step.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param email - The email that the user is creating an account with.
 */
export function accountPasswordSet(blogId: string, email: string) {
  return Amplitude.track("subscribe_modal_account_password_set", {
    blogId,
    email,
  })
}

/**
 * Track when the user clicks the "Create account" button in the CreateAccount step,
 * but setting the password failed.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param email - The email that the user is creating an account with.
 */
export function accountPasswordSetError(blogId: string, email: string) {
  return Amplitude.track("subscribe_modal_account_password_set_error", {
    blogId,
    email,
  })
}

/**
 * Track when the user clicks the "Login" button in the Login step after entering their email and password.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param email - The email that the user is logging in with.
 */
export function loginSubmittedViaEmail(blogId: string, email: string) {
  return Amplitude.track("subscribe_modal_login_submitted_via_email", {
    blogId,
    email,
  })
}

/**
 * Track when the user clicks the "Login" button in the Login step after connecting their wallet.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param walletAddress - The wallet address that the user is logging in with.
 */
export function loginSubmittedViaWallet(blogId: string, walletAddress: string) {
  return Amplitude.track("subscribe_modal_login_submitted_via_wallet", {
    blogId,
    walletAddress,
  })
}

/**
 * Track when the user clicks the "Login" button in the Login step, but the login failed.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param email - The email that the user is logging in with.
 * @param error - The error that occurred.
 */
export function loginError(blogId: string, email: string, error: string) {
  return Amplitude.track("subscribe_modal_login_error", {
    blogId,
    email,
    error,
  })
}

/**
 * Track when the user clicks the "Next" button in the Recommendations step.
 *
 * @param referrerBlogId - The ID of the blog that is recommending other newsletters.
 */
export function recommendationNextClicked(referrerBlogId: string) {
  return Amplitude.track("subscribe_modal_recommendation_next_clicked", {
    referrerBlogId,
  })
}

/**
 * Track when the user clicks the "Subscribe" button for a recommendation.
 *
 * @param referrerBlogId - The ID of the blog that is recommending other newsletters.
 * @param recommendationId - The ID of the recommendation that the user is subscribing to.
 */
export function recommendationSubscribeClicked(
  referrerBlogId: string,
  recommendationId: string
) {
  return Amplitude.track("subscribe_modal_recommendation_subscribe_clicked", {
    referrerBlogId,
    recommendationId,
  })
}

/**
 * Track when the user clicks the "Subscribe" button for all recommendations.
 *
 * @param referrerBlogId - The ID of the blog that is recommending other newsletters.
 * @param recommendationIds - The IDs of the recommendations that the user is subscribing to.
 */
export function recommendationSubscribeAllClicked(
  referrerBlogId: string,
  recommendationIds: string[]
) {
  return Amplitude.track(
    "subscribe_modal_recommendation_subscribe_all_clicked",
    {
      referrerBlogId,
      recommendationIds,
    }
  )
}

/**
 * Track when the user clicks the "Next" button in the ChooseCommunities step.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param optedIn - The communities that the user opted in to.
 * @param optedOut - The communities that the user opted out of.
 */
export function chooseCommunitiesClicked(
  blogId: string,
  optedIn: string[],
  optedOut: string[]
) {
  return Amplitude.track("subscribe_modal_choose_communities_clicked", {
    blogId,
    optedIn,
    optedOut,
  })
}

/**
 * Track when the user successfully chooses communities.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 */
export function chooseCommunitiesSuccess(blogId: string) {
  return Amplitude.track("subscribe_modal_choose_communities_success", {
    blogId,
  })
}

/**
 * Track when the user clicks the "Next" button in the ChooseCommunities step,
 * but the choose communities failed.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 * @param error - The error that occurred.
 */
export function chooseCommunitiesError(blogId: string, error: string) {
  return Amplitude.track("subscribe_modal_choose_communities_error", {
    blogId,
    error,
  })
}

/**
 * Track when the user is already subscribed to a blog.
 *
 * @param blogId - The ID of the blog that the user is already subscribed to.
 */
export async function alreadySubscribed(blogId: string) {
  const user = await getUser()

  const email = user ? ("email" in user ? user.email : "") : ""

  return Amplitude.track("subscribe_modal_already_subscribed", {
    blogId,
    email,
  })
}

/**
 * Track when the user successfully subscribes to a blog.
 *
 * @param blogId - The ID of the blog that the user is subscribing to.
 */
export async function success(blogId: string) {
  const user = await getUser()

  const email = user ? ("email" in user ? user.email : "") : ""

  return Amplitude.track("subscribe_modal_success", {
    blogId,
    email,
  })
}
